import { Check, X } from "lucide-react"
import { cn } from "@/lib/cn"
import { wordDiff, type DiffSegment } from "@/lib/word-diff"

// Fields where `before`/`after` are long-form prose and a unified
// word-level diff is far more readable than two side-by-side panels.
const PROSE_FIELDS = new Set(["prompt_md", "description", "notes", "name"])

export type Proposal = {
  id: string
  kind: string
  input: Record<string, unknown>
  /** Operator-side: has the operator ticked this proposal for apply? */
  accepted: boolean
  /** Per-skill monotonic counter assigned at persist time. Stable across
      apply/cancel so #34 always means "the 34th proposal for this skill". */
  number?: number
}

type Props = {
  proposal: Proposal
  onToggle: (id: string) => void
  onRemove: (id: string) => void
  /** The current persisted skill — used to render before/after diffs. */
  skillSnapshot: Record<string, unknown>
  /** Persisted scenarios — used to render the real before-state for scenario_update proposals. */
  scenarios?: Array<Record<string, unknown>>
}

const KIND_LABEL: Record<string, string> = {
  propose_field_update:           "Update field",
  propose_prompt_section_update:  "Update prompt section",
  propose_tool_keys_update:       "Replace tool_keys",
  propose_tool_config_update:     "Update tool_config",
  propose_data_collection_update: "Replace data_collection",
  propose_scenario_add:           "Add scenario",
  propose_scenario_update:        "Update scenario",
  propose_scenario_remove:        "Remove scenario",
}

// Same H2 section convention the backend uses (Portal::Skills::PromptSections).
const SECTION_HEADING_RE = /^##\s+(.+?)\s*$/gm
const PREAMBLE_NAME = "<preamble>"

function extractSectionBody(promptMd: string, sectionName: string): string {
  const text = promptMd ?? ""
  const wanted = sectionName.trim().toLowerCase()
  if (wanted === PREAMBLE_NAME) {
    const firstHeading = text.match(/^##\s+/m)
    if (!firstHeading) return text
    return text.slice(0, firstHeading.index ?? 0).trim()
  }
  const matches: Array<{ name: string; start: number; bodyStart: number }> = []
  let m: RegExpExecArray | null
  SECTION_HEADING_RE.lastIndex = 0
  while ((m = SECTION_HEADING_RE.exec(text)) !== null) {
    matches.push({ name: m[1].trim(), start: m.index, bodyStart: m.index + m[0].length })
  }
  for (let i = 0; i < matches.length; i++) {
    if (matches[i].name.toLowerCase() === wanted) {
      const end = i + 1 < matches.length ? matches[i + 1].start : text.length
      return text.slice(matches[i].bodyStart, end).trim()
    }
  }
  return ""
}

export function ProposalDiffCard({ proposal, onToggle, onRemove, skillSnapshot, scenarios = [] }: Props) {
  const summary = (proposal.input.summary as string | undefined) ?? ""
  const label = KIND_LABEL[proposal.kind] ?? proposal.kind

  return (
    <div
      className={cn(
        "rounded-control border px-3 py-2 text-xs transition",
        proposal.accepted
          ? "border-vip-gold/40 bg-vip-goldMute"
          : "border-border bg-white/[0.03]",
      )}
    >
      <header className="mb-1.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onToggle(proposal.id)}
            className={cn(
              "flex h-5 w-5 shrink-0 items-center justify-center rounded border transition",
              proposal.accepted
                ? "border-vip-gold/40 bg-vip-gold/20 text-vip-goldSoft"
                : "border-border-strong text-app-subtle hover:bg-white/5",
            )}
            aria-label={proposal.accepted ? "Reject proposal" : "Accept proposal"}
          >
            {proposal.accepted && <Check size={12} strokeWidth={3} />}
          </button>
          {proposal.number !== undefined && proposal.number !== null && (
            <span className="rounded-chip border border-border bg-white/5 px-1.5 py-0.5 font-mono text-[10px] text-app-subtle">
              #{proposal.number}
            </span>
          )}
          <span className="font-medium text-app-text">{label}</span>
          {(proposal.input.field as string | undefined) && (
            <code className="rounded bg-white/5 px-1 text-[10px] text-app-muted">
              {String(proposal.input.field)}
            </code>
          )}
          {(proposal.input.section as string | undefined) && (
            <code className="rounded bg-white/5 px-1 text-[10px] text-app-muted">
              ## {String(proposal.input.section)}
            </code>
          )}
          {(proposal.input.tool_key as string | undefined) && (
            <code className="rounded bg-white/5 px-1 text-[10px] text-app-muted">
              {String(proposal.input.tool_key)}
            </code>
          )}
        </div>
        <button
          type="button"
          onClick={() => onRemove(proposal.id)}
          className="text-app-subtle hover:text-app-text"
          aria-label="Discard proposal"
        >
          <X size={12} strokeWidth={2} />
        </button>
      </header>

      {summary && <p className="mb-1.5 text-app-muted">{summary}</p>}

      <Diff proposal={proposal} skillSnapshot={skillSnapshot} scenarios={scenarios} />
    </div>
  )
}

function Diff({ proposal, skillSnapshot, scenarios }: { proposal: Proposal; skillSnapshot: Record<string, unknown>; scenarios: Array<Record<string, unknown>> }) {
  const after = proposal.input.after
  const fieldKey = String(proposal.input.field ?? "")

  switch (proposal.kind) {
    case "propose_field_update": {
      const before = skillSnapshot[fieldKey]
      if (PROSE_FIELDS.has(fieldKey)) {
        return <ProseDiff before={typeof before === "string" ? before : ""} after={typeof after === "string" ? after : ""} />
      }
      return <SideBySide before={before} after={after} />
    }
    case "propose_prompt_section_update": {
      const sectionName = String(proposal.input.section ?? "")
      const promptMd    = (skillSnapshot.promptMd as string | undefined) ?? ""
      const before      = extractSectionBody(promptMd, sectionName)
      return <ProseDiff before={before} after={typeof after === "string" ? after : ""} />
    }
    case "propose_tool_keys_update": {
      const before = (skillSnapshot.toolKeys as string[]) ?? []
      return <ArrayDiff before={before} after={Array.isArray(after) ? (after as string[]) : []} />
    }
    case "propose_tool_config_update": {
      const tk = String(proposal.input.tool_key ?? "")
      const allConfigs = (skillSnapshot.toolConfigs as Record<string, unknown>) ?? {}
      const before = allConfigs[tk]
      return <SideBySide before={before} after={after} />
    }
    case "propose_data_collection_update": {
      const before = (skillSnapshot.dataCollection as unknown[]) ?? []
      return <SideBySide before={before} after={after} />
    }
    case "propose_scenario_add":
      return <ScenarioFieldDiff before={null} after={after as Record<string, unknown> | null} />
    case "propose_scenario_update": {
      const sid = Number(proposal.input.scenario_id)
      const before = scenarios.find((s) => Number(s.id) === sid) ?? null
      return <ScenarioFieldDiff before={before} after={after as Record<string, unknown> | null} />
    }
    case "propose_scenario_remove":
      return <p className="text-app-muted">Detach scenario #{String(proposal.input.scenario_id)}</p>
    default:
      return <pre className="overflow-auto text-[10px] text-app-muted">{JSON.stringify(proposal.input, null, 2)}</pre>
  }
}

// Unified word-level prose diff. No before/after toggle — the
// "Current setup" tab already shows the persisted state, and the
// after-text is implicit in the diff (green + unchanged segments).
// Brand-new sections (empty before) render entirely as additions.
function ProseDiff({ before, after }: { before: string; after: string }) {
  const segments: DiffSegment[] = wordDiff(before, after)
  const isCreation = before.trim().length === 0
  const stats = isCreation ? null : countChanges(segments)

  return (
    <div className="text-[11px]">
      {stats && (stats.adds > 0 || stats.dels > 0) && (
        <div className="mb-1 flex items-center gap-2 text-[10px] text-app-subtle">
          {isCreation
            ? <span className="text-vip-goldSoft">new section</span>
            : <span>edits</span>}
          {stats.adds > 0  && <span className="text-status-success">+{stats.adds}</span>}
          {stats.dels > 0  && <span className="text-status-error">−{stats.dels}</span>}
        </div>
      )}
      <div className="max-h-64 overflow-auto whitespace-pre-wrap rounded border border-border bg-app-bg p-2 leading-relaxed text-app-text">
        {segments.length === 0 ? (
          <span className="text-app-muted">(no change)</span>
        ) : (
          segments.map((s, i) => <DiffChunk key={i} seg={s} />)
        )}
      </div>
    </div>
  )
}

function DiffChunk({ seg }: { seg: DiffSegment }) {
  if (seg.type === "equal") return <span className="text-app-muted">{seg.text}</span>
  if (seg.type === "add") {
    return (
      <span className="rounded bg-status-success/15 text-status-success">
        {seg.text}
      </span>
    )
  }
  return (
    <span className="rounded bg-status-error/15 text-status-error line-through decoration-status-error/60">
      {seg.text}
    </span>
  )
}

function countChanges(segs: DiffSegment[]): { adds: number; dels: number } {
  let adds = 0, dels = 0
  for (const s of segs) {
    if (s.type === "add") adds += s.text.replace(/\s+/g, " ").trim().split(" ").filter(Boolean).length
    else if (s.type === "del") dels += s.text.replace(/\s+/g, " ").trim().split(" ").filter(Boolean).length
  }
  return { adds, dels }
}

// Scenario diff renderer. Walks the proposed `after` object's top-level
// keys and only renders rows where the value actually changed. Each
// changed field gets a renderer matched to its type:
//   - text strings (persona, goal, attacker_instructions, name) → ProseDiff
//   - turns / assertions arrays                                  → JSON SideBySide (per-item structured)
//   - everything else                                            → SideBySide
//
// "before = null" means a brand-new scenario (propose_scenario_add).
// In that case every after-field renders as a creation (green-only).
//
// Fields the operator/system shouldn't see in the diff are filtered:
// scenario_id (routing only), id, created_at/updated_at.
const HIDDEN_SCENARIO_FIELDS = new Set([
  "scenario_id", "id", "created_at", "updated_at", "skill_id", "summary"
])
const TEXT_SCENARIO_FIELDS = new Set([
  "key", "name", "category", "scope", "persona", "goal",
  "attacker_instructions", "notes"
])
function ScenarioFieldDiff({
  before, after,
}: {
  before: Record<string, unknown> | null
  after:  Record<string, unknown> | null
}) {
  const a = after ?? {}
  const b = before ?? {}

  const keys = Object.keys(a).filter((k) => !HIDDEN_SCENARIO_FIELDS.has(k))
  const changedKeys = keys.filter((k) => !shallowEqual(b[k], a[k]))

  if (changedKeys.length === 0) {
    return <p className="text-[11px] text-app-subtle">No field changes.</p>
  }

  return (
    <div className="space-y-3">
      {changedKeys.map((k) => (
        <div key={k}>
          <div className="mb-0.5 text-[10px] uppercase tracking-wide text-app-subtle">{k}</div>
          {TEXT_SCENARIO_FIELDS.has(k) ? (
            <ProseDiff
              before={typeof b[k] === "string" ? (b[k] as string) : ""}
              after={typeof a[k] === "string"  ? (a[k] as string) : ""}
            />
          ) : (
            <SideBySide before={b[k] ?? null} after={a[k]} />
          )}
        </div>
      ))}
    </div>
  )
}

// Shallow-equal for diff filtering — compares JSON shape so arrays /
// objects with identical content don't show as "changed."
function shallowEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  try { return JSON.stringify(a) === JSON.stringify(b) } catch { return false }
}

function SideBySide({ before, after }: { before: unknown; after: unknown }) {
  return (
    <div className="grid grid-cols-2 gap-2 text-[10px]">
      <div>
        <div className="mb-0.5 text-app-subtle">before</div>
        <pre className="max-h-32 overflow-auto rounded border border-border bg-app-bg p-1.5 text-app-muted">
{fmt(before)}
        </pre>
      </div>
      <div>
        <div className="mb-0.5 text-vip-goldSoft">after</div>
        <pre className="max-h-32 overflow-auto rounded border border-vip-gold/30 bg-vip-goldMute p-1.5 text-app-text">
{fmt(after)}
        </pre>
      </div>
    </div>
  )
}

function ArrayDiff({ before, after }: { before: string[]; after: string[] }) {
  const added   = after.filter((x) => !before.includes(x))
  const removed = before.filter((x) => !after.includes(x))
  return (
    <div className="text-[10px]">
      {added.length > 0 && (
        <div>
          <span className="text-status-success">+ </span>
          {added.map((x) => <code key={x} className="ml-1 rounded bg-status-success/10 px-1 text-status-success">{x}</code>)}
        </div>
      )}
      {removed.length > 0 && (
        <div className="mt-0.5">
          <span className="text-status-error">- </span>
          {removed.map((x) => <code key={x} className="ml-1 rounded bg-status-error/10 px-1 text-status-error">{x}</code>)}
        </div>
      )}
      {added.length === 0 && removed.length === 0 && (
        <p className="text-app-muted">No change.</p>
      )}
    </div>
  )
}

function fmt(v: unknown): string {
  if (v == null) return "—"
  if (typeof v === "string") return v
  try {
    return JSON.stringify(v, null, 2)
  } catch {
    return String(v)
  }
}
